// Tile — board ToileWallRest (`.t`, `.hv`, `.doors`) + the done-transition hairline (`.hairline`).
// The cell-at-work layers themselves (breath/numeral/frame) moved to `WorkingCell.module.scss`
// (2026-09-12 carrier fix) — one definition shared with the armed view's result slot (`Column.tsx`,
// `Sheet.tsx`); `.tile` below keeps `container-type: inline-size` so the numeral it hosts still
// scales to the cell's own width. Colours, radii, type and shadows are the house's `--tl-*` tokens
// (`tokens.ts`).

.tile {
    position: relative;
    margin: 0;
    border-radius: var(--tl-radius-art-xs);
    overflow: hidden;
    background: var(--tl-surface);
    min-width: 0;
    cursor: pointer;
    // The place numeral (below) is sized in container-query units so it scales with the cell's
    // OWN width rather than the viewport — a 72px board cell and a ~110px phone cell each read a
    // proportionate numeral, never a fixed pixel size fighting the layout at either end.
    container-type: inline-size;

    &:focus-within .doors {
        opacity: 1;
        pointer-events: auto;
    }

    // The pack-page ↗ specifically — a skip-link: visible only once keyboard focus lands anywhere
    // in the cell, never on a mouse hover alone (operator, 2026-09-19; `WordDoors.tsx`'s own
    // `arrowReveal: 'focus'` doc states the cross-file contract this global, unscoped classname is —
    // CSS Modules give this file no way to select WordDoors' own scoped `.arrow`). `.doors` above
    // going to `opacity: 1` on EITHER hover or focus-within does not by itself make this element
    // visible: its own `opacity: 0` (`WordDoors.module.scss`) is independent of its parent once the
    // parent's opacity is nonzero, so it stays hidden through a hover and only this rule — keyed on
    // `:focus-within` alone, never `:hover` — raises it.
    &:focus-within :global(.wall-arrow-focus-only) {
        opacity: 1;
        pointer-events: auto;
    }

    // The visitor's door (`Tile.tsx`'s `door` prop) opens on the same two signals as `.doors`, and
    // never takes the pointer: it is presentation inside the cell's own button, so it keeps
    // `pointer-events: none` at every state rather than the `auto` the interactive doors need.
    &:focus-within .visitorDoor {
        opacity: 1;
    }

    &:focus-within::after {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--tl-scrim-light);
        pointer-events: none;
    }

    // Hover exists only where a pointer can hover. iOS Safari plays `:hover` on the FIRST tap and,
    // when that hover changes what is on screen, keeps the click for a second tap — so the doors
    // opened where the sheet should have risen (operator-observed, 2026-09-02; BRIEF: "Phone: no
    // hover, one gesture — tap the picture and the sheet rises armed"). `Tile.tsx` gates its
    // mouse-driven state the same way.
    @media (hover: hover) {
        &:hover .doors {
            opacity: 1;
            pointer-events: auto;
        }

        &:hover .visitorDoor {
            opacity: 1;
        }

        &:hover::after {
            content: '';
            position: absolute;
            inset: 0;
            background: var(--tl-scrim-light);
            pointer-events: none;
        }
    }
}

.image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    // The fade is keyed on the picture's own `load` (Tile.tsx), not on the element mounting: a
    // cached picture is already complete at first commit and appears at once, a slow one fades in
    // when its pixels arrive rather than over an empty box.
    opacity: 0;
    transition: opacity 200ms ease;
}

.imageIn {
    opacity: 1;
}

// The quick look — "tap the picture = armed, nothing fired" (BRIEF §5) — is its own control,
// laid over the picture and UNDER the doors, so the cell holds no nested interactives.
.picture {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid var(--tl-accent);
        outline-offset: -2px;
    }
}

// The done-transition hairline — tungsten, one beat (`Tile.tsx`'s `DONE_FLASH_MS`, ~1.2s), then
// removed. Gated by a fixed-duration mount rather than a CSS animation (`transitionsToDone` in
// Tile.tsx), so a cell already `done` at mount (page load, a catalog scroll) never renders this
// element at all, and reduced motion needs no override here — there is no motion to reduce, only a
// timed appearance of a static mark.
.hairline {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    height: 1px;
    background: var(--tl-accent);
    pointer-events: none;
}

.doors {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 12px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
}

// The words group: the chip doors (or the plain title/direction, chip-less) — `.doors`' own
// single child now that no cell carries a Generate door alongside it
// (second-look ruling, 2026-09-19: no cell offers a gesture besides arming).
.wordsGroup {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 1;
}

// Bounds `WordDoors`' own row (which `WordDoors.tsx` already applies alongside this class — no
// `composes` needed, a cross-file `composes: X from './Y.module.scss'` loads `Y` RAW, unprocessed
// by Sass; `_chipDress.scss`'s own header has the confirmed failure) to the group's own width — the
// reason `.chips` inside it actually wraps (and its fixed-height clip actually clips) rather than
// growing past a narrow cell (`WordDoors.module.scss`'s own header doc has the full CSS rationale).
// `min-width: 0` — without it, a flex item's automatic minimum size floors at its content's own
// min-content width, which for an unshrinkable (`white-space: nowrap`) chip is its full text width;
// that floor would propagate up through this element and defeat `.chip`'s own `max-width: 100%`
// clamp below it (second-look fix: a lone over-wide chip must truncate, not force this row wider
// than the cell).
.tileChips {
    flex: 1;
    min-width: 0;
}

// A chip-less tile (every Ovid tile today) or a from-scratch/pinned cell with nothing typed yet:
// the title/blank-basis label, plain — not a door, not a link (board `WordsAsDoors` §5).
.plain {
    font-family: var(--tl-font-label);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tl-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

// The visitor's door — the board's "2 · hover" state (`OvidVeilStates.dc.html`): a small line over
// a filled pill on a veiled cell, the pill alone on an open one (operator ruling, 2026-09-17). Its
// own class rather than a modifier on `.doors`: `.doors` earns `pointer-events: auto` on hover for
// the label link it holds, and this layer must never take a tap away from the picture button
// beneath it.
.visitorDoor {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
}

.visitorLine {
    font-family: var(--tl-font-label);
    font-size: 9.5px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--tl-muted);
}

.visitorCta {
    text-align: center;
    font-family: var(--tl-font);
    font-size: 12.5px;
    color: var(--tl-accent-ink);
    background: var(--tl-accent);
    border-radius: var(--tl-radius-chrome);
    padding: 7px 13px;
}

// The wall's motion is decoration: with reduced motion asked for, a picture simply appears. The
// breath's own reduced-motion pulse lives with the rest of the cell-at-work layers, in
// `WorkingCell.module.scss` — nothing left to park here but the picture's fade-in.
@media (prefers-reduced-motion: reduce) {
    .image {
        transition: none;
        opacity: 1;
    }
}
